import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TrendingDown, Zap, Check, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtPrice(p) {
  return p != null ? `€${parseFloat(p).toFixed(2)}` : "—";
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function FareAlertsPanel({ requestId }) {
  const { getAuthHeaders } = useAuth();
  const [alerts, setAlerts]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/fare-alerts/request/${requestId}`,
        { headers: getAuthHeaders() }
      );
      setAlerts(data);
    } catch {
      // silently skip — panel is supplementary
    } finally {
      setLoading(false);
    }
  }, [requestId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const acknowledge = async (alertId) => {
    try {
      await axios.post(
        `${API}/fare-alerts/${alertId}/acknowledge`,
        {},
        { headers: getAuthHeaders() }
      );
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, acknowledged: true } : a));
      toast.success("Alert dismissed");
    } catch {
      toast.error("Could not dismiss alert");
    }
  };

  if (loading || alerts.length === 0) return null;

  const unread = alerts.filter(a => !a.acknowledged).length;

  return (
    <div className="mx-4 mt-3 mb-1 rounded-lg border border-purple-200 bg-purple-50 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-purple-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-purple-600 shrink-0" />
          <span className="text-sm font-semibold text-purple-900">
            Fare Monitor
          </span>
          {unread > 0 && (
            <Badge className="bg-purple-600 text-white text-[10px] px-1.5 py-0 h-4">
              {unread} new
            </Badge>
          )}
          <span className="text-xs text-purple-500">
            {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="w-4 h-4 text-purple-400" />
          : <ChevronDown className="w-4 h-4 text-purple-400" />}
      </button>

      {expanded && (
        <div className="divide-y divide-purple-100">
          {alerts.map(alert => (
            <div
              key={alert.id}
              className={`px-4 py-3 flex items-start justify-between gap-3 ${
                alert.acknowledged ? "opacity-50" : ""
              }`}
            >
              <div className="flex items-start gap-2 min-w-0">
                {alert.is_first_discovery
                  ? <Zap className="w-4 h-4 text-purple-500 mt-0.5 shrink-0" />
                  : <TrendingDown className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                }
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900 font-mono">
                      {alert.origin} → {alert.destination}
                    </span>
                    <span className="text-xs text-slate-500">{alert.departure_date}</span>
                    {!alert.is_first_discovery && (
                      <Badge className="bg-emerald-100 text-emerald-800 text-[10px] px-1.5 py-0 h-4 font-medium">
                        −€{alert.delta_eur?.toFixed(2)} ({alert.delta_pct?.toFixed(1)}%)
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    {alert.is_first_discovery ? (
                      <span className="text-sm text-emerald-700 font-semibold">
                        First fare: {fmtPrice(alert.new_price)}
                      </span>
                    ) : (
                      <span className="text-sm text-slate-700">
                        <span className="line-through text-slate-400">{fmtPrice(alert.previous_price)}</span>
                        {" → "}
                        <span className="font-semibold text-emerald-700">{fmtPrice(alert.new_price)}</span>
                      </span>
                    )}
                    {alert.airline && (
                      <span className="text-xs text-slate-500">
                        {alert.airline}
                        {alert.stops != null && ` · ${alert.stops === 0 ? "non-stop" : `${alert.stops} stop${alert.stops > 1 ? "s" : ""}`}`}
                        {alert.duration && ` · ${alert.duration}`}
                      </span>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-400">{fmtDate(alert.created_at)}</span>
                </div>
              </div>

              {!alert.acknowledged && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => acknowledge(alert.id)}
                  className="shrink-0 h-7 w-7 p-0 text-purple-400 hover:text-purple-700 hover:bg-purple-100"
                  title="Dismiss"
                >
                  <Check className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
